import { makeAutoObservable, runInAction } from 'mobx';

import { CatalogRequests } from '~/api/Catalog';
import { mainStore, userStore } from '~/stores';
import { Product } from '~/stores/CategoriesStore/Product';
import { Task } from '~/stores/shared/Task';
import { catalogWarehouseParam } from '~/utils/catalogWarehouse';

import type { RecommendationCollection, RecommendationConfig } from './types';

export class RecommendationStore {
  public products: Product[] = [];
  public readonly productsRequest = new Task(this.loadProducts.bind(this));
  private readonly config: RecommendationConfig;
  private abortController: AbortController | null = null;

  constructor(config: RecommendationConfig) {
    this.config = config;
    makeAutoObservable<RecommendationStore, 'abortController' | 'config'>(
      this,
      {
        abortController: false,
        config: false,
      },
    );
  }

  public get collection(): RecommendationCollection | null {
    if (!this.products.length && this.productsRequest.isFulfilled) {
      return null;
    }

    const products = this.config.sortProducts
      ? this.config.sortProducts(this.products)
      : this.products;

    return {
      id: this.config.id,
      nameKey: this.config.nameKey,
      linkId: this.config.linkId,
      products,
      productsRequest: this.productsRequest,
    };
  }

  public clearProducts(): void {
    this.products = [];
  }

  private async loadProducts(): Promise<void> {
    this.abortController?.abort();
    this.abortController = new AbortController();

    if (!userStore.isAuthorized) {
      runInAction(() => {
        this.products = [];
      });
      return;
    }

    try {
      const params = {
        ...catalogWarehouseParam(),
        source: this.config.source,
      };

      const { data } =
        this.config.source === 'top_picks'
          ? await CatalogRequests.getTopPicksRecommendations(
              params as { warehouseCode: string; source: 'top_picks' },
              { signal: this.abortController.signal },
            )
          : await CatalogRequests.getOrderAgainRecommendations(
              params as { warehouseCode: string; source: 'order_again' },
              { signal: this.abortController.signal },
            );

      runInAction(() => {
        this.products = data
          .filter(Product.validate)
          .map((p) => new Product(p));
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      mainStore.errorHandler(error as never, `load${this.config.id}Products`);
      runInAction(() => {
        this.products = [];
      });
    }
  }
}
